import { Formik, FormikHelpers } from 'formik';
import get from 'lodash.get';
import React, { useContext } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import { Link, RouteComponentProps } from 'react-router-dom';
import { object } from 'yup';

import userApiUtil from 'src/api/loggedInUser';
import apiUtil from 'src/api/userManagement';
import FormikSubmit from 'src/components/FormikSubmit';
import { AppContext } from 'src/containers/Core/AppContext';
import { setUser } from 'src/containers/Core/AppReducer';
import { AppViewContext } from 'src/containers/Core/AppView';
import { SIGN_UP } from 'src/containers/Core/Paths';
import { login } from 'src/utils/common';
import { emailSchema, notEmptySchema } from 'src/validationSchemas';

type SigninProps = {
  setImg?: (img: string) => void;
} & RouteComponentProps<{}, {}, Partial<{ email: string; token: string }>>;

type FormValues = {
  email: string;
  password: string;
};

const Signin: React.FunctionComponent<SigninProps> = ({ location: { state = { email: null, token: null } } }) => {
  const { dispatch } = useContext(AppContext);

  const onSubmit = async (data: FormValues, actions: FormikHelpers<FormValues>) => {
    try {
      const response = await apiUtil.signIn({ ...data, ...(state.token ? { token: state.token } : {}) });
      login(response.access, response.refresh);
      const userResponse = await userApiUtil.getLoggedInUserDetails();
      dispatch(setUser(userResponse));
    } catch (errors) {
      if (errors.response.status === 400) {
        actions.setErrors(get(errors, 'response.data'));
      } else {
        actions.setErrors({ email: get(errors, 'response.data.error') });
      }
    } finally {
      actions.setSubmitting(false);
    }
  };

  return (
    <>
      <SigninTitle />
      <SigninForm defaultEmail={state.email} onSubmit={onSubmit} />
    </>
  );
};

export default Signin;

// ────────────────────────────────────────────────────────────────────────────────

const SigninTitle: React.FunctionComponent = () => (
  <>
    <h2 className={`mb-2 font-weight-bold text-primary`}>Sign In to Open Rewards</h2>
    <h6 className={'mb-4 text-muted'}>Attract customers and grow your business!</h6>
  </>
);

// ────────────────────────────────────────────────────────────────────────────────

type SigninFormProps = {
  onSubmit: FormikOnSubmit<FormValues>;
  defaultEmail: string;
};

const schema = object({
  email: emailSchema,
  password: notEmptySchema,
});

const SigninForm: React.FunctionComponent<SigninFormProps> = ({ onSubmit, defaultEmail }) => {
  const { isPhoneView, isTabView } = useContext(AppViewContext);

  return (
    <Formik validationSchema={schema} onSubmit={onSubmit} initialValues={{ email: defaultEmail || '', password: '' }}>
      {({ errors, handleChange, values, handleSubmit, touched, isSubmitting }) => (
        <Form noValidate onSubmit={handleSubmit}>
          <Form.Group controlId="formBasicEmail">
            <Form.Label>Email Address</Form.Label>
            <Form.Control
              type="email"
              disabled={isSubmitting}
              placeholder="example_name@gmail.com"
              value={values.email}
              onChange={handleChange}
              isInvalid={touched.email && !!errors.email}
              autoComplete="off"
              name="email"
              maxLength={255}
            />
            <Form.Control.Feedback type="invalid">{errors.email}</Form.Control.Feedback>
          </Form.Group>

          <Form.Group controlId="formBasicPassword">
            <Form.Label>Password</Form.Label>
            <Form.Control
              type="password"
              disabled={isSubmitting}
              placeholder="Enter password"
              isInvalid={touched.password && !!errors.password}
              value={values.password}
              onChange={handleChange}
              autoComplete="off"
              name="password"
              maxLength={255}
            />
            <Form.Control.Feedback type="invalid">{errors.password}</Form.Control.Feedback>
          </Form.Group>
          <FormikSubmit
            variant="primary"
            block
            className="font-weight-bold mt-3"
            loading={isSubmitting}
            text="Sign In"
          />
          <Row className={`${isPhoneView && !isTabView ? 'text-center flex-column-reverse' : ''} my-3`}>
            <Col sm={12} md={5} lg={5}>
              <Link to="/forgot-password" className={`font-weight-bold text-decoration-none`}>
                Forgot password?
              </Link>
            </Col>
            <Col sm={12} md={7} lg={7} className={`${!isPhoneView || isTabView ? 'text-right' : ''}`}>
              <Link to={SIGN_UP} className={`ml-1 font-weight-bold text-decoration-none`}>
                Back to Sign Up
              </Link>
            </Col>
          </Row>
        </Form>
      )}
    </Formik>
  );
};
